import Head from "next/head";
import Link from "next/link";
import { Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    group: "How It Works",
    answer: (
      <>
        <b>Hall of Fame Opening Stretch Pass</b> - Includes an assigned seat in
        the Upper View Level of sections <b>509 & 510</b>. If Sections 509 and
        510 are unavailable for Pass holders, The Reds will provide an
        alternative seating assignment. Pass holders do have the ability to
        upgrade to an assigned seat at an additional cost, subject to
        availability. Note: You will not be able to upgrade your Opening Day
        seat.
      </>
    ),
  },
  {
    group: "How It Works",
    answer: (
      <>
        The <b>Hall of Fame Opening Stretch Pass</b> is a mobile-only program
        available exclusively through the free MLB Ballpark app (required for
        ticket delivery).
      </>
    ),
  },
  {
    group: "How It Works",
    answer: (
      <>
        On the morning before each homestand, you will receive a text to opt-in
        for the game in that homestand. <i>*Excludes Opening Day*</i> See FAQs
        for more information.
      </>
    ),
  },
  {
    group: "How It Works",
    answer:
      "When you're ready to enter Great American Ball Park, access your passes in the MLB Ballpark app, scan in with your barcode and enjoy the game from your designated area!",
  },
  {
    group: "How It Works",
    answer: (
      <>
        <b>Please note:</b> All your pass tickets will be available via the MLB
        Ballpark app on your iPhone or Android phone. There are no
        refunds/exchanges. Tickets are non-transferable.{" "}
        <b>
          Hall of Fame Opening Stretch Pass tickets may not be resold or offered
          for resale to third parties
        </b>
        . Any attempted resale of these tickets may result in the deactivation
        of such tickets; holders of deactivated tickets will not be permitted to
        enter the ballpark. Deactivated tickets will be refunded to the original
        form of payment, minus applicable service fees.
      </>
    ),
  },
  {
    group: "How It Works",
    answer: (
      <>
        <b>
          If the Reds determine, in their sole discretion, that any ticket buyer
          purchases tickets for the primary purpose of reselling those tickets,
          then those purchases will be canceled without notice.
        </b>
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "What is the Hall of Fame Opening Stretch Pass?",
    answer:
      "The Hall of Fame Opening Stretch Pass gives you the ability to attend all March and April home games including Opening Day!",
  },
  {
    group: "Frequently Asked Questions",
    question: "When is the check-in window?",
    answer: (
      <>
        The check-in window opens at <b>9am</b> on the first day of each
        homestand. You will be notified via text when the period is open.
        <br />
        Note: You will NOT have to check in for Opening Day. Your Opening Day
        ticket will be available in your MLB Ballpark App in February-March
        timeframe.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Does the Hall of Fame Opening Stretch Pass include an assigned seat?",
    answer: (
      <>
        The Pass will typically include an assigned seat in the Upper View Level
        of sections <b>509 & 510</b>. If Sections 509 and 510 are unavailable
        for Pass holders, The Reds will provide an alternative seating
        assignment. Pass holders do have the ability to upgrade to an assigned
        seat at an additional cost, subject to availability. Note: You will not
        be able to upgrade your Opening Day seat.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Can we be seated together if our passes were purchased under different orders?",
    answer:
      "Yes! Simply create a group in your account and add the friends or family you want to sit with—just make sure they have purchased a pass as well.",
  },
  {
    group: "Frequently Asked Questions",
    question: "How do I upgrade my seats?",
    answer:
      "Open the MLB Ballpark App and click on the Reds tab at the bottom of the screen. Select the Seat Upgrade tile. Select your new seating location and enter the number of tickets you'd like to upgrade. Add payment and check out. The upgraded tickets will be added to the ticket tab of your MLB Ballpark App. Note: Upgrades are available a few hours before game time and are subject to availability.",
  },
  {
    group: "Frequently Asked Questions",
    question: "When do I need to request guest tickets?",
    answer: (
      <>
        Guest tickets need to be requested no later than four hours before
        scheduled first pitch. <b>*Excludes Opening Day*</b>
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "Where are my tickets delivered?",
    answer:
      "All of your tickets will be delivered digitally via the MLB Ballpark app.",
  },
  {
    group: "Frequently Asked Questions",
    question: "When will my tickets appear in my MLB Ballpark App?",
    answer: (
      <>
        Tickets will be delivered to your MLB Ballpark App account 3 hours prior
        to first pitch. <br />
        Opening Day tickets will be available in your MLB Ballpark App in
        February-March timeframe.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "Are there any penalties if I do not opt in?",
    answer: (
      <>
        There are no penalties for not attending a game, however you do need to
        confirm your attendance (opt-in) to receive a ticket.{" "}
        <b>*Excludes Opening Day*</b>
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Can the Hall of Fame Opening Stretch Pass tickets be transferred to a friend?",
    answer:
      "Passes cannot be shared/forwarded to other parties. Admission to Great American Ball Park will be managed through the MLB Ballpark app on your mobile device. Multiple packages can be purchased if you and the rest of your party enter at the same time.",
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Can I sell my Hall of Fame Opening Stretch Pass for games that I do not attend?",
    answer:
      "No, Hall of Fame Opening Stretch Passes are non-transferable and are only available on the phone of the Hall of Fame Opening Stretch Pass purchaser. Hall of Fame Opening Stretch Passes, guest passes, and upgraded tickets cannot be resold. Violators will be removed from the program.",
  },
  {
    group: "Frequently Asked Questions",
    question: "What happens if a game time changes?",
    answer:
      "All game times are subject to change. Your Pass ticket will be valid only for the date of the game and is non-exchangeable.",
  },
  {
    group: "Frequently Asked Questions",
    question: "What if one of my games is rained out or postponed?",
    answer:
      "If a game is postponed your Hall of Fame Opening Stretch Pass ticket will be valid for the rescheduled game only.",
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Will I be considered a Season Ticket Member? Do I have access to Season Ticket Member events?",
    answer:
      "No, Hall of Fame Opening Stretch Pass holders are not eligible for Season Ticket Member benefits. Tickets are not transferable and cannot be exchanged for a later date.",
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Does the Hall of Fame Opening Stretch Pass include postseason access?",
    answer:
      "No, the Hall of Fame Opening Stretch Pass is not considered a season plan and does not guarantee any postseason access.",
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Are any other events included with the Hall of Fame Opening Stretch Pass?",
    answer:
      "The Hall of Fame Opening Stretch Pass is only valid for regular-season home games. It does not include tickets to non-game events at Great American Ball Park.",
  },
  {
    group: "Frequently Asked Questions",
    question:
      "How can I get a refund and cancel my Hall of Fame Opening Stretch Pass?",
    answer: (
      <>
        By selecting the Place Order button, you agree to purchase the tickets
        you have selected. No Refunds. No Exchanges. Please visit{" "}
        <Link
          target="_blank"
          href="https://www.mlb.com/reds/tickets/info/terms-conditions"
        >
          Reds.com/terms
        </Link>{" "}
        for more information. You can also contact us at{" "}
        <Link href="mailto:ticketservices@reds.com">
          ticketservices@reds.com
        </Link>
        .
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Why does the log-in page look different when I'm redirected to the buying page?",
    answer:
      "The MLB log-in page may look different, but it's the same log-in process. You can use your MLB account to sign in.",
  },
  {
    group: "Frequently Asked Questions",
    question:
      "Can I use a different email than my registered Hall of Fame Membership?",
    answer:
      "No, offer is only valid to the email registered under your Hall of Fame Membership.",
  },
  {
    group: "Frequently Asked Questions",
    question: "Is there a deadline to purchase?",
    answer: (
      <>
        The deadline to purchase is <b>February 18</b>.
      </>
    ),
  },
  {
    group: "Frequently Asked Questions",
    question: "Where can I find a full list of terms and conditions?",
    answer: (
      <>
        All Reds game tickets, including the Hall of Fame Opening Stretch Pass,
        are subject to the terms and conditions listed at{" "}
        <Link href="https://www.reds.com/terms" target="_blank">
          reds.com/terms
        </Link>
        .
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    In connection with your purchase, you will receive ticket-related commercial
    emails from{" "}
    <Link href="http://reds.com" target="_blank">
      reds.com
    </Link>
    . You may unsubscribe at any time. By checking this box you agree to receive
    additional commercial emails from reds.com and MLB.com.
    <br />
    The terms and conditions of your ticket and policies applicable to its use
    are updated to address circumstances related to COVID-19 and health and
    safety. By clicking the Checkout button, you agree to accept all Terms and
    Conditions. Further, due to the COVID-19 pandemic, scheduling may be
    impacted; games may be canceled, rescheduled, or postponed or games may be
    held with limited capacity. Upon completing your purchase by selecting
    Checkout you acknowledge and agree that your credit card will be charged.
    <br />
    By clicking Checkout, you agree to the MLB Privacy Policy, Terms of Use and
    to receive your ticket(s) via an SMS text message to the mobile # associated
    with your Reds account. Message and data rates may apply. Tickets will be
    made available in the MLB Ballpark mobile application only.{" "}
    <Link href="http://MLB.com" target="_blank">
      MLB.com
    </Link>{" "}
    and supported mobile device required.
  </>
);

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self",
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self",
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/reds/tickets/info/terms-conditions?partnerId=redirect-cin-terms",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        General TicketingTerms & Conditions | Cincinnati Reds
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-tik-tok-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !(function (w, d, t) {
                w.TiktokAnalyticsObject = t;
                var ttq = (w[t] = w[t] || []);
                (ttq.methods = [
                  "page",
                  "track",
                  "identify",
                  "instances",
                  "debug",
                  "on",
                  "off",
                  "once",
                  "ready",
                  "alias",
                  "group",
                  "enableCookie",
                  "disableCookie",
                ]),
                  (ttq.setAndDefer = function (t, e) {
                    t[e] = function () {
                      t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                    };
                  });
                for (var i = 0; i < ttq.methods.length; i++)
                  ttq.setAndDefer(ttq, ttq.methods[i]);
                (ttq.instance = function (t) {
                  for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
                    ttq.setAndDefer(e, ttq.methods[n]);
                  return e;
                }),
                  (ttq.load = function (e, n) {
                    var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
                    (ttq._i = ttq._i || {}),
                      (ttq._i[e] = []),
                      (ttq._i[e]._u = i),
                      (ttq._t = ttq._t || {}),
                      (ttq._t[e] = +new Date()),
                      (ttq._o = ttq._o || {}),
                      (ttq._o[e] = n || {});
                    n = document.createElement("script");
                    (n.type = "text/javascript"),
                      (n.async = !0),
                      (n.src = i + "?sdkid=" + e + "&lib=" + t);
                    e = document.getElementsByTagName("script")[0];
                    e.parentNode.insertBefore(n, e);
                  });

                ttq.load("CBP9R33C77UB6N07V05G");
                ttq.page();
                console.log('tiktok pixel page view fired.');
              })(window, document, "ttq");

              window.addEventListener("${constants.customEvents.purchaseSuccess}", async function(e) {
                const { cartRes, checkoutRes, passes, user } = e.detail;

                ttq.identify({
                  email: user.email,
                  phone_number: "+1" + user.phoneNumber,
                });

                if(cartRes.products && cartRes.products.length) {
                  for(let product of cartRes.products){
                    ttq.track("CompletePayment", {
                      content_id: product.eventCode,
                      content_name: product.productTitle,
                      quantity: String(product.seatCount),
                      price: String(product.price),
                      value: String(product.price * product.seatCount),
                      currency: "USD",
                    });
                    await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                  }
                };



                if(cartRes.passes && cartRes.passes.length && passes.length ) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_id = passDetail.eventCode;
                  content_name = passDetail.passTitle;
                  price = String(passDetail.price);
                  quantity = String(cartRes.passes[0].seatCount);

                  ttq.track("CompletePayment", {
                    content_id: passDetail.eventCode,
                    content_name: passDetail.passTitle,
                    quantity: String(cartRes.passes[0].seatCount),
                    price: String(passDetail.price),
                    value: String(cartRes.totalAmount),
                    currency: "USD",
                  });
                };
                console.log('tik tok pixel purchase event fired.');
              });
            `,
        }}
      />

      <script
        defer
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '368362963346231');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                try{
                  const { cartRes, checkoutRes, passes } = e.detail;

                  let content_name = "";
                  if(cartRes.products && cartRes.products.length) {
                    content_name = "${TEAM_SCHEMA}_single_game_purchases";
                  };
                  if(cartRes.passes && cartRes.passes.length && passes.length ) {
                    const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                    content_name = passDetail.passTitle;
                  };

                  fbq('track', 'Purchase', {
                    content_name: content_name, 
                    value: String(cartRes.totalAmount), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                
                } catch(error){
                  console.log('meta pixel purchase event error:', error); 
                }
              });
            `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=368362963346231&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };
